import {
  ArrowCounterClockwise,
  Article,
  BookOpen,
  BowlFood,
  type Icon,
  Sparkle,
} from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { Badge, type BadgeTone } from "../primitives/badge";

/**
 * Content / library card. Used by /plan's "Browse the library" region
 * to render Articles, Stories, Recipes that aren't actionable
 * microsteps - just things Heartbeat may surface, sorted by theme.
 *
 * Structure: optional poster (140px tall, full-bleed) with the kind
 * chip overlaid bottom-left, then a body with title + desc + foot.
 *
 * Distinct from PlanActionCard: that card carries status + when/why +
 * actions for items currently in the user's plan. ContentCard is
 * library-only, browse-only - no actions.
 */
export type ContentCardKind = "habit" | "reset" | "recipe" | "article" | "story" | "microstep";

export interface ContentCardProps {
  kind: ContentCardKind;
  title: ReactNode;
  desc?: ReactNode;
  /** Bottom hairline line. e.g. "5 min read · last surfaced Wed". */
  foot?: ReactNode;
  /** Optional poster image URL. */
  poster?: string | null;
  className?: string;
  onClick?: () => void;
}

const KIND_TO_BADGE: Record<ContentCardKind, BadgeTone> = {
  habit: "habit",
  reset: "reset",
  recipe: "recipe",
  article: "article",
  story: "story",
  microstep: "story",
};

const KIND_LABEL: Record<ContentCardKind, string> = {
  habit: "Habit",
  reset: "Reset",
  recipe: "Recipe",
  article: "Article",
  story: "Story",
  microstep: "Microstep",
};

const KIND_ICON: Record<ContentCardKind, Icon> = {
  habit: Sparkle,
  reset: ArrowCounterClockwise,
  recipe: BowlFood,
  article: Article,
  story: BookOpen,
  microstep: Sparkle,
};

export function ContentCard({
  kind,
  title,
  desc,
  foot,
  poster,
  className,
  onClick,
}: ContentCardProps) {
  const interactive = Boolean(onClick);
  const Root = interactive ? "button" : "article";
  const KindIcon = KIND_ICON[kind];
  return (
    <Root
      type={interactive ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "flex flex-col overflow-hidden rounded-2xl border border-border-default bg-surface-elevated text-left",
        interactive ? "cursor-pointer transition-shadow duration-200 hover:shadow-card-hover" : "",
        className,
      )}
    >
      {poster ? (
        <div
          aria-hidden="true"
          className="relative h-[140px] w-full bg-cover bg-center"
          style={{ backgroundImage: `url(${poster})` }}
        >
          <span className="absolute bottom-2.5 left-2.5">
            <Badge tone={KIND_TO_BADGE[kind]}>{KIND_LABEL[kind]}</Badge>
          </span>
        </div>
      ) : (
        <div
          aria-hidden="true"
          className="relative grid h-[140px] w-full place-items-center bg-surface-canvas bg-teal-dots text-ink-brand"
        >
          <KindIcon size={30} weight="duotone" />
          <span className="absolute bottom-2.5 left-2.5">
            <Badge tone={KIND_TO_BADGE[kind]}>{KIND_LABEL[kind]}</Badge>
          </span>
        </div>
      )}
      <div className="flex flex-col gap-1.5 px-4 pt-3 pb-3.5">
        <h3 className="m-0 font-title text-[18px] font-medium leading-snug tracking-tight text-ink-primary">
          {title}
        </h3>
        {desc ? <p className="m-0 text-sm leading-relaxed text-ink-secondary">{desc}</p> : null}
        {foot ? (
          <p className="m-0 mt-1.5 border-t border-border-default pt-2 font-sans text-xs font-medium text-ink-tertiary">
            {foot}
          </p>
        ) : null}
      </div>
    </Root>
  );
}
